import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/apiFetch';
import '../styles/ReimbursementFlow.css';

// ── T&C declarations (mirrors _TC_QUESTIONS in the agent) ────────────────────
const TC_QUESTIONS = [
  {
    id: 'tc_worked_from_home',
    label:
      'I have worked from home and used my personal broadband/internet services to support business continuity.',
  },
  {
    id: 'tc_no_company_dongle',
    label: "I confirm I don't have an internet dongle provided by the company.",
  },
  {
    id: 'tc_not_on_deputation',
    label: 'I am NOT currently on outbound (LTX or STX) deputation.',
  },
  {
    id: 'tc_single_entity',
    label:
      'I am NOT working for any other Bosch legal entity/location that is not part of RBIN.',
  },
  {
    id: 'tc_not_on_leave',
    label:
      'I am NOT on maternity leave, child raising leave, sabbatical leave, long medical leave, or any other extended leave during the claim period.',
  },
  {
    id: 'tc_certify_true',
    label:
      'I certify that the information I provide is true and to the best of my knowledge. (I will be liable for any company actions if found false)',
  },
];

// ── Generate last 12 months ──────────────────────────────────────────────────
function getRollingMonths() {
  const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const now = new Date();
  const months = [];
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    months.push({
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      label: `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`,
      year: d.getFullYear(),
      month: d.getMonth() + 1,
    });
  }
  return months;
}

const STEPS = { TC: 'tc', MONTHS: 'months', RESULT: 'result', ERROR: 'error' };

/**
 * ReimbursementFlow — a self-contained frontend form for internet reimbursement.
 *
 * Props:
 *   onDone(resultMessage: string)  — called with the success/error string to inject into chat
 *   onCancel()                     — called when user dismisses the flow
 *   preSelectedMonth               — optional "{year}-{month}" string from the chat message
 */
export default function ReimbursementFlow({ onDone, onCancel, preSelectedMonth }) {
  const { user } = useAuth();
  const [step, setStep] = useState(STEPS.TC);
  const [tcValues, setTcValues] = useState({});
  const [selectedMonths, setSelectedMonths] = useState(
    preSelectedMonth ? [preSelectedMonth] : []
  );
  const [loading, setLoading] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [alreadyClaimed, setAlreadyClaimed] = useState([]);

  const allMonths = getRollingMonths();
  const allTcAccepted = TC_QUESTIONS.every((q) => tcValues[q.id] === true);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const toggleTc = (id) =>
    setTcValues((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleMonth = (value) =>
    setSelectedMonths((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );

  const handleTcSubmit = async () => {
    if (!allTcAccepted) return;
    setLoading(true);
    try {
      const resp = await apiFetch('/reimburse/status');
      if (resp.ok) {
        const data = await resp.json();
        const claimed = (data.window_months || [])
          .filter((m) => m.claimed)
          .map((m) => m.value);
        setAlreadyClaimed(claimed);
      }
    } catch (err) {
      console.warn('Failed to fetch claim status:', err);
    } finally {
      setLoading(false);
    }
    setStep(STEPS.MONTHS);
  };

  const handleMonthSubmit = async () => {
    if (selectedMonths.length === 0 || !user?.ntid) return;
    setLoading(true);

    try {
      // Call /reimburse/submit directly — no LLM, exact year/months from the frontend Date()
      const resp = await apiFetch('/reimburse/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pre_selected_months: selectedMonths,   // already in "YYYY-M" format from getRollingMonths()
          tc_context: Object.fromEntries(TC_QUESTIONS.map((q) => [q.id, true])),
        }),
      });

      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        throw new Error(`Server error ${resp.status}: ${errText.slice(0, 200)}`);
      }

      const data = await resp.json();
      const status = data.status || '';
      const msg = data.message || '';

      if (status === 'error') {
        setResultMessage(msg || 'An error occurred submitting the claim.');
        setStep(STEPS.ERROR);
        return;
      }

      if (data.months_claimed?.length) {
        const labels = data.months_claimed
          .map((mv) => allMonths.find((m) => m.value === mv)?.label || mv)
          .join(', ');
        setResultMessage(
          `Your internet allowance claim for **${labels}** has been submitted successfully.\n\n` +
          (data.total_amount ? `Total amount: ₹${Number(data.total_amount).toLocaleString()}` : '')
        );
      } else {
        setResultMessage(msg || 'Claim submitted successfully.');
      }
      setStep(STEPS.RESULT);
    } catch (err) {
      setResultMessage(`Failed to submit claim: ${err.message}`);
      setStep(STEPS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onDone?.(resultMessage);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="reimb-flow">
      <div className="reimb-flow__header">
        <span className="reimb-flow__icon">🌐</span>
        <div>
          <div className="reimb-flow__title">Internet Reimbursement</div>
          <div className="reimb-flow__sub">
            {step === STEPS.TC && 'Step 1 of 2 — Declarations'}
            {step === STEPS.MONTHS && 'Step 2 of 2 — Select Months'}
            {step === STEPS.RESULT && 'Claim Submitted'}
            {step === STEPS.ERROR && 'Submission Failed'}
          </div>
        </div>
        <button className="reimb-flow__close" onClick={onCancel} title="Dismiss">✕</button>
      </div>

      {/* ── Step 1: T&C ───────────────────────────────────────────────────── */}
      {step === STEPS.TC && (
        <div className="reimb-flow__body">
          <p className="reimb-flow__intro">
            Please confirm the following declarations to proceed with your claim.
          </p>
          <div className="reimb-flow__tc-list">
            {TC_QUESTIONS.map((q) => (
              <label key={q.id} className={`reimb-flow__tc-item${tcValues[q.id] ? ' checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!tcValues[q.id]}
                  onChange={() => toggleTc(q.id)}
                  className="reimb-flow__checkbox"
                />
                <span className="reimb-flow__tc-label">{q.label}</span>
              </label>
            ))}
          </div>
          <div className="reimb-flow__footer">
            <button className="reimb-flow__btn reimb-flow__btn--secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="reimb-flow__btn reimb-flow__btn--primary"
              disabled={!allTcAccepted || loading}
              onClick={handleTcSubmit}
            >
              {loading ? 'Loading…' : 'I Accept — Continue →'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Month picker ──────────────────────────────────────────── */}
      {step === STEPS.MONTHS && (
        <div className="reimb-flow__body">
          <p className="reimb-flow__intro">
            Select the month(s) you want to claim for (last 12 months eligible):
          </p>
          <div className="reimb-flow__month-grid">
            {allMonths.map((m) => {
              const isClaimed = alreadyClaimed.includes(m.value);
              const isSelected = selectedMonths.includes(m.value);
              return (
                <button
                  key={m.value}
                  className={`reimb-flow__month-btn${isSelected ? ' selected' : ''}${isClaimed ? ' claimed' : ''}`}
                  onClick={() => !isClaimed && toggleMonth(m.value)}
                  disabled={isClaimed}
                  title={isClaimed ? 'Already claimed' : ''}
                >
                  {m.label}
                  {isClaimed && <span className="reimb-flow__claimed-badge">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="reimb-flow__footer">
            <button
              className="reimb-flow__btn reimb-flow__btn--secondary"
              onClick={() => setStep(STEPS.TC)}
            >
              ← Back
            </button>
            <button
              className="reimb-flow__btn reimb-flow__btn--primary"
              disabled={selectedMonths.length === 0 || loading}
              onClick={handleMonthSubmit}
            >
              {loading ? 'Submitting…' : `Submit Claim (${selectedMonths.length} month${selectedMonths.length !== 1 ? 's' : ''})`}
            </button>
          </div>
        </div>
      )}

      {/* ── Result / Error ────────────────────────────────────────────────── */}
      {(step === STEPS.RESULT || step === STEPS.ERROR) && (
        <div className="reimb-flow__body">
          <div className={`reimb-flow__result${step === STEPS.ERROR ? ' reimb-flow__result--error' : ''}`}>
            <div className="reimb-flow__result-icon">
              {step === STEPS.RESULT ? '✅' : '❌'}
            </div>
            <p className="reimb-flow__result-text">{resultMessage}</p>
          </div>
          <div className="reimb-flow__footer reimb-flow__footer--center">
            <button className="reimb-flow__btn reimb-flow__btn--primary" onClick={handleDone}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
